import { Center } from '@mantine/core';
import QRCode from 'react-qr-code';
import { useEffect, useState } from 'react';
import { useMedplum } from '@medplum/react';

interface QRCodeDisplayProps {
  patientId: string | undefined;
}

export function QRCodeDisplay({ patientId }: QRCodeDisplayProps): JSX.Element {
  const medplum = useMedplum();
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSignedUrl = async () => {
      try {
        // Get the current user's token
        const token = await medplum.getAccessToken();
        if (!token) {
          throw new Error('No authentication token found');
        }

        const apiKey = import.meta.env.VITE_COPILOT_API_KEY;
        if (!apiKey) {
          console.error('API key not found in environment variables');
          throw new Error('API key configuration missing');
        }

        console.log('Making request with headers:', {
          Authorization: 'Bearer [TOKEN]',
          'X-API-Key': apiKey.substring(0, 8) + '...',
          patientId
        });

        const response = await fetch('https://api.copilothealth.com/v1/capture', {
          method: 'POST',
          headers: {
            'X-API-Key': apiKey,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            patientId: patientId,
          }),
        });

        if (!response.ok) {
          let errorText;
          try {
            const errorData = await response.json();
            errorText = JSON.stringify(errorData);
          } catch {
            errorText = await response.text();
          }
          
          console.error('API Error Response:', {
            status: response.status,
            statusText: response.statusText,
            headers: Object.fromEntries(response.headers.entries()),
            errorText
          });
          
          throw new Error(`Failed to get signed URL: ${response.status} ${response.statusText} - ${errorText}`);
        }

        const data = await response.json();
        if (!data.signedUrls?.source) {
          console.error('Unexpected API response format:', data);
          throw new Error('Invalid response format from server');
        }

        setSignedUrl(data.signedUrls.source);
      } catch (err) {
        console.error('Error fetching signed URL:', err);
        setError(err instanceof Error ? err.message : 'Failed to get signed URL');
      }
    };

    if (patientId) {
      fetchSignedUrl();
    }
  }, [patientId, medplum]);

  if (error) {
    return <Center p="xl">Error: {error}</Center>;
  }

  if (!signedUrl) {
    return <Center p="xl">Loading...</Center>;
  }

  return (
    <Center p="xl" style={{ flexDirection: 'column', gap: '1rem' }}>
      <QRCode value={signedUrl} size={200} />
      <div style={{ wordBreak: 'break-all', maxWidth: '80%', textAlign: 'center' }}>
        {signedUrl}
      </div>
    </Center>
  );
}